import PropTypes from 'prop-types';
import React from 'react';

const headerCellStyle = {
  fontWeight: "600",
  fontSize: "14px",
  color: "var(--od-c-999999, #999999)",
  textAlign: "right",
  padding: "6px 9px 6px 0px"
};

const SettlementVenueNetTable = ({
  offer,
  showSummary,
  isSettling,
  requestFormat,
  colgroup,
  formatCurrency,
  formatConvertedCurrency,
  currencyConversionIsEnabled,
  showCurrencyConversion,
  secondaryCurrencySymbol,
  isSavingShowOnPdf,
  onShowOnPdfChange,
}) => {
  const isWorksheet = requestFormat === "html";
  const isVisible = isWorksheet || offer.display_venue_net_on_settlement_pdf;
  const isHiddenFromSupportAct = !isWorksheet && offer.support_settlement_simplified;

  if (!isSettling) return null;
  if (!offer.settlement_includes_headliner_payout) return null;
  if (isHiddenFromSupportAct) return null;
  if (!isVisible) return null;

  const amountRow = ({ testId, label, amount, negate, emphasize }) => {
    const sign = negate && amount > 0 ? "-" : "";
    const emphasis = (content) => (emphasize ? <strong>{content}</strong> : content);

    return (
      <tr data-testid={testId} style={emphasize ? {fontSize: "16px"} : null}>
        <td style={{padding: "3px 9px 3px 15px"}}>{emphasis(label)}</td>
        <td style={{padding: "3px 0px"}}><span>&nbsp;</span></td>
        <td style={{padding: "3px 0px"}}><span>&nbsp;</span></td>
        {currencyConversionIsEnabled ? (
          <td style={{padding: "3px 9px 3px 0px", textAlign: "right"}}>
            {showCurrencyConversion ? `${sign}${formatConvertedCurrency(amount)}` : <span>&nbsp;</span>}
          </td>
        ) : null}
        <td style={{padding: "3px 15px 3px 0px", textAlign: "right"}}>
          {emphasis(`${sign}${formatCurrency(amount)}`)}
        </td>
        <td style={{padding: "3px 0px"}}><span>&nbsp;</span></td>
      </tr>
    );
  };

  return (
    <tr>
      <td colSpan={2}>
        <div style={{marginTop: "8px"}} data-testid="settlement-venue-net">
          <table style={{width: "100%", borderSpacing: "0px 2px", borderCollapse: "separate"}}>
            {colgroup}
            <thead style={{textAlign: "left"}}>
              <tr style={{background: "var(--od-c-f5f5f5, #F5F5F5)"}}>
                <th style={{fontSize: "14px", color: "var(--od-c-000000, #000000)", padding: "6px 15px", textAlign: "left"}}>
                  <strong>Venue Net</strong>
                  {isWorksheet ? (
                    <div className="custom-control custom-checkbox custom-checkbox-table d-inline-block ml-3">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        checked={!!offer.display_venue_net_on_settlement_pdf}
                        disabled={!!isSavingShowOnPdf}
                        onChange={(e) => onShowOnPdfChange(e.target.checked)}
                        id="offer-display-venue-net-on-settlement-pdf"
                      />
                      <label
                        className="custom-control-label"
                        style={{fontSize: "14px", fontWeight: "normal", color: "var(--od-c-414651, #414651)"}}
                        htmlFor="offer-display-venue-net-on-settlement-pdf"
                      >
                        Show on PDF
                      </label>
                    </div>
                  ) : null}
                </th>
                <th style={headerCellStyle}>&nbsp;</th>
                <th style={headerCellStyle}>&nbsp;</th>
                {currencyConversionIsEnabled ? (
                  <th style={headerCellStyle}>
                    {showCurrencyConversion ? secondaryCurrencySymbol.toUpperCase() : <span>&nbsp;</span>}
                  </th>
                ) : null}
                <th style={{...headerCellStyle, padding: "6px 15px 6px 0px"}}>TOTAL</th>
                <th style={{padding: "6px 0px"}}><span>&nbsp;</span></th>
              </tr>
            </thead>
            <tbody style={{fontSize: "14px"}}>
              {amountRow({
                testId: "settlement-venue-net-receipts",
                label: "Net Box Office Receipts",
                amount: showSummary.netBoxOfficeReceipts,
              })}
              {amountRow({
                testId: "settlement-venue-net-costs",
                label: "Expenses + Artist Payout",
                amount: showSummary.expensesAndArtistPayout,
                negate: true,
              })}
              {amountRow({
                testId: "settlement-venue-net-total",
                label: "Venue Net",
                amount: showSummary.venueNetExcludingAncillary,
                emphasize: true,
              })}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
};

SettlementVenueNetTable.propTypes = {
  offer: PropTypes.object.isRequired,
  showSummary: PropTypes.object.isRequired,
  isSettling: PropTypes.bool,
  requestFormat: PropTypes.string,
  colgroup: PropTypes.node.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  formatConvertedCurrency: PropTypes.func.isRequired,
  currencyConversionIsEnabled: PropTypes.bool,
  showCurrencyConversion: PropTypes.bool,
  secondaryCurrencySymbol: PropTypes.string,
  isSavingShowOnPdf: PropTypes.bool,
  onShowOnPdfChange: PropTypes.func.isRequired,
};

export default SettlementVenueNetTable;
